<script setup lang="ts">
import { getMonth, getYear } from 'date-fns';
import { computed } from 'vue';

import type { IExportDataSchema } from '@modules/export-data/types';
import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import VDownloadReportToast from '@modules/download-report/components/VDownloadReportToast/VDownloadReportToast.vue';
import { useDownloadReportToast } from '@modules/download-report/composables';
import { TRANSLATION_GROUP_EXPORT_DATA } from '@modules/export-data/constants';
import { useExportDataGenerateMutation } from '@modules/export-data/mutations';
import { useExportDataTypesQuery } from '@modules/export-data/queries';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';

const { getTranslation } = useI18n();
const { data: types, isLoading } = useExportDataTypesQuery();
const { mutateAsync: generateReport, isLoading: isGenerateLoading } = useExportDataGenerateMutation();
const { showToast } = useDownloadReportToast();

const typesOptions = computed<IFormSelectInputOption<string>[]>(() => {
  if (!types.value) {
    return [];
  }

  return types.value.list.map((value) => ({
    label: value,
    value,
  }));
});

const currentDate = new Date();

const schema = computed<FormSchema<IExportDataSchema>>(() => ({
  report_type: {
    type: EFormInputTypes.SELECT,
    options: typesOptions.value,
    autoSelectFirst: true,
    required: true,
    label: getTranslation(TRANSLATION_GROUP_EXPORT_DATA, 'REPORT_TYPE').value,
  },
  period: {
    type: EFormInputTypes.DATE,
    value: currentDate,
    required: true,
    view: 'month',
    dateFormat: 'mm/yy',
    label: getTranslation(TRANSLATION_GROUP_EXPORT_DATA, 'REPORT_PERIOD').value,
  },
}));

async function onSubmit(values: IExportDataSchema): Promise<void> {
  await generateReport({
    report_type: values.report_type,
    period_month: getMonth(values.period) + 1,
    period_year: getYear(values.period),
  });

  showToast();
}
</script>

<template>
  <VCard v-data-qa-auto-id="'ExportDataFormPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_EXPORT_DATA, 'HEADER')"
  >
    <VLoader
      :loading="isLoading"
    >
      <VForm
        :schema="schema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_EXPORT_DATA, 'CREATE')"
        :is-loading="isGenerateLoading"
        @submit="onSubmit"
      />
    </VLoader>
    <VDownloadReportToast />
  </VCard>
</template>
